import {Location} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Injector,
    OnInit,
    Renderer2,
    ViewContainerRef
} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import * as _ from 'lodash-es';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {destroy$} from '../../components/destroyable';
import {Widget, WidgetId} from '../../components/layout/widget/widget';
import {WidgetDefinition} from '../../components/layout/widget/widget-definition';
import {WidgetSettings} from '../../components/layout/widget/widget-settings';
import {WidgetSettingsDefinition} from '../../components/layout/widget/widget-settings-definition';
import {WidgetFactory} from '../../components/layout/widget/widget.factory';
import {Watchlist, WatchlistService} from '../../components/watchlist';
import {UrlHelperService} from '../../services/url-helper.service';
import {WindowRefService} from '../../services/window-ref.service';
import {FakeFloatLayoutService} from '../dashboard/float-layout/fake-float-layout.service';
import {FloatLayoutService} from '../dashboard/float-layout/float-layout.service';
import {CellSizeObserverService} from '../dashboard/layout/cell/cell-size-observer.service';
import {WidgetSettingsComponentService} from '../dashboard/layout/widget/widget-settings';
import {DetachedWidgetDefinition, DetachedWidgetPageDefinition} from './detached-widget-def';
import {IS_DETACHED_WIDGET} from './detached-widget-injection';
import {WidgetPageCellSizeObserverService} from './widget-page-cell-size-observer.service';

export interface WidgetPageRouteData {
    detachedWidgetPageDef: DetachedWidgetPageDefinition;
}

export interface WidgetSettingsWithWatchlist extends WidgetSettings {
    watchlist?: Watchlist;
}

const CHANGES_DEBOUNCE_TIME = 100;

@Component({
    selector: 'widget-page',
    templateUrl: './widget-page.component.html',
    styleUrls: ['./widget-page.component.scss'],
    providers: [
        WidgetSettingsComponentService, // It's provided here since the component should own a control on this service.
        { provide: FloatLayoutService, useClass: FakeFloatLayoutService },
        { provide: CellSizeObserverService, useClass: WidgetPageCellSizeObserverService },
        { provide: IS_DETACHED_WIDGET, useValue: true }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WidgetPageComponent implements OnInit {

    public isWidgetBodyHovered: boolean;

    public activeWidget: Widget;
    public activeWidgetIndex: number;
    public widgets: Widget[] = [];
    public widgetSettings: WidgetSettingsDefinition;
    public watchlist: Watchlist;

    public readonly destroy$ = destroy$();
    private detachedWidgetDefMap = new Map<WidgetId, DetachedWidgetDefinition>();

    private readonly urlUpdate$: Subject<void> = new Subject<void>();

    constructor(
        public injector: Injector,
        public elementRef: ElementRef,
        public viewContainerRef: ViewContainerRef,
        public renderer: Renderer2,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected widgetFactory: WidgetFactory,
        private floatLayoutService: FloatLayoutService,
        private watchlistService: WatchlistService,
        private windowRefService: WindowRefService,
        private titleService: Title,
        private location: Location,
        private widgetSettingsComponentService: WidgetSettingsComponentService
    ) {}

    public ngOnInit(): void {
        const detachedWidgetDefs: DetachedWidgetDefinition[] =
            (this.activatedRoute.snapshot.data as WidgetPageRouteData).detachedWidgetPageDef.detachedWidgetDefs;

        this.activeWidgetIndex =
            (this.activatedRoute.snapshot.data as WidgetPageRouteData).detachedWidgetPageDef.activeWidgetIndex;

        detachedWidgetDefs.forEach(detachedWidgetDef => this.createDetachedWidgetFromDef(detachedWidgetDef));

        this.activeWidget = this.widgets[this.activeWidgetIndex];

        this.urlUpdate$.pipe(
            debounceTime(CHANGES_DEBOUNCE_TIME)
        ).subscribe(() => this.updateUrl());

        this.widgetSettingsComponentService.setLayerRef(this.viewContainerRef);
    }

    public setActiveWidget(widget: Widget): void {
        this.activeWidget = widget;
        this.activeWidgetIndex = this.widgets.indexOf(widget);

        this.urlUpdate$.next();
    }

    private deleteWidget(widget: Widget): void {
        this.widgets = this.widgets.filter(currentWidget => widget.id !== currentWidget.id);
        this.detachedWidgetDefMap.delete(widget.id);

        this.urlUpdate$.next();
    }

    private updateUrl(): void {
        const detachedWidgetDefs: DetachedWidgetDefinition[] = [];
        this.detachedWidgetDefMap
            .forEach(detachedWidgetDefMapItem => detachedWidgetDefs.push(detachedWidgetDefMapItem));

        const detachedWidgetPageDef: DetachedWidgetPageDefinition = {
            detachedWidgetDefs,
            activeWidgetIndex: this.activeWidgetIndex
        };

        const base64EncodedSettings = UrlHelperService.encodeDataToBase64(detachedWidgetPageDef);
        const url = this.router
            .createUrlTree([`../${base64EncodedSettings}`], {relativeTo: this.activatedRoute})
            .toString();

        this.location.go(url);
    }

    private createDetachedWidgetFromDef(detachedWidgetDef: DetachedWidgetDefinition): void {

        // reading widget definition
        const widgetDef: WidgetDefinition = {
            id: '',
            tabId: '',
            cellId: '',
            widgetTypeId: detachedWidgetDef.widgetTypeId,
            settings: detachedWidgetDef.settings
        };

        if (detachedWidgetDef.watchlist) {
            const watchlist: Watchlist = detachedWidgetDef.watchlist;
            const watchlistFromService = this.watchlistService.findWatchlistById(watchlist.id);
            this.watchlist = watchlist;

            if (watchlistFromService) {
                watchlistFromService.symbols = watchlist.symbols;
                watchlistFromService.name = watchlist.name;
            } else {
                this.watchlistService.addWatchlist(watchlist);
            }
        }

        const widget: Widget = this.widgetFactory.createWidgetByDefinition(widgetDef);
        this.widgets.push(widget);

        this.detachedWidgetDefMap.set(widget.id, detachedWidgetDef);

        this.widgetSettings = widgetDef.settings;
        widget.settingsWithUpdates$
            .subscribe((settings: Partial<WidgetSettingsWithWatchlist>) => {
                this.widgetSettings = {
                    ...this.widgetSettings,
                    ...this.widgetFactory.getWidgetDefinition(widget).settings
                };

                if (settings.watchlist) {
                    this.watchlist = settings.watchlist;
                }

                const newDetachedWidgetDef = {
                    widgetTypeId: detachedWidgetDef.widgetTypeId,
                    settings: _.mapValues(
                        this.widgetSettings,
                        value => value === undefined ? null : value
                    ) as WidgetSettingsDefinition,
                    ...(this.watchlist && {watchlist: this.watchlist})
                } as DetachedWidgetDefinition;

                this.detachedWidgetDefMap.set(widget.id, newDetachedWidgetDef);
                this.urlUpdate$.next();
            });

        widget.title$.subscribe(title => {
            const detachedWidgetsDef = this.detachedWidgetDefMap.get(widget.id);
            detachedWidgetsDef.settings.title = title;
            this.detachedWidgetDefMap.set(widget.id, detachedWidgetsDef);
            this.updatePageTitle();
        });

        widget.destroy$.subscribe(() => {

            if (this.widgets.length !== 1) {
                this.deleteWidget(widget);
                this.setActiveWidget(this.widgets[0]);
            } else {
                this.windowRefService.nativeWindow.close();
            }
        });
    }

    private updatePageTitle(): void {
        const titles: string[] = [];
        this.detachedWidgetDefMap.forEach(detachedWidgetDef => {
            titles.push(detachedWidgetDef.settings.title);
        });

        this.titleService.setTitle(titles.join(', '));
    }
}
